import React, { useEffect, useMemo, useState } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { getIndentClass } from '../../utils/styleUtils';

import CommittedSection from './CommittedSection';
import SponsorMilestoneData from './SponsorMilestoneData';
import UncommittedSection from './UncommittedSection';

import { formatFigure } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function SponsorRow(props) {
    const [milestonesCount, setMilestonesCount] = useState(null);
    const [isAddMilestoneAllowed, setIsAddMilestoneAllowed] = useState(false);
    const [showChildren, setShowChildren] = useState(false);

    // Calculate indentation level
    const indentLevel = props.indentLevel || 2;
    const childIndentLevel = indentLevel + 1;

    useEffect(() => {
        setMilestonesCount(props.sponsor.currentMilestones.length);
        setIsAddMilestoneAllowed(props.sponsor.currentMilestones.length < props.periods.length);
    }, [props.sponsor]);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'rows') {
            setShowChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    const toggleShow = () => {
        setShowChildren(!showChildren);
    };

    // Calculate total periods count from the periods structure
    const periodsKeys = Object.keys(props.periods || {});
    const maxSponsorMilestonesCount = Math.max(props.maxSponsorMilestonesCount, periodsKeys.length);

    const totals = useMemo(() => {
        return calculateRowTotals(props.sponsor.currentMilestones);
    }, [props.sponsor.currentMilestones]);

    const hasUncommittedData = useMemo(() => {
        return (
            props.sponsor.uncommitted &&
            props.sponsor.uncommitted.milestones &&
            (props.sponsor.uncommitted.value > 0 ||
                (props.sponsor.uncommitted.milestones.cash && props.sponsor.uncommitted.milestones.cash.length > 0) ||
                (props.sponsor.uncommitted.milestones.vik && props.sponsor.uncommitted.milestones.vik.length > 0))
        );
    }, [props.sponsor.uncommitted]);

    const hasCommittedData = useMemo(() => {
        const committed = props.sponsor.committed;
        if (!committed) return false;

        if (Array.isArray(committed)) {
            return committed.length > 0 && committed.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        } else if (typeof committed === 'object') {
            const contracts = Object.values(committed);
            return contracts.length > 0 && contracts.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        }

        return false;
    }, [props.sponsor.committed]);

    return (
        <>
            {milestonesCount !== null && (
                <tr className="!tw-bg-amber-50">
                    <td className="!tw-text-charcoal-75">
                        <div className={`tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer ${getIndentClass(indentLevel)}`} onClick={toggleShow}>
                            <div className="tw-font-medium tw-text-blue">{props.sponsor.name}</div>
                            <div>{showChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                        </div>
                    </td>
                    <td className="tw-p-4"></td>
                    <td className="tw-p-4 tw-text-right tw-border-r-2">{formatFigure(props.sponsor.value)}</td>
                    {props.sponsor.currentMilestones &&
                        [...Array(maxSponsorMilestonesCount)].map((e, i) => {
                            return (
                                <React.Fragment key={`th_milestones_${i}_date_percentage`}>
                                    <SponsorMilestoneData
                                        key={`${props.keyPrefix || ''}tr_sponsor_${props.sponsor.id}_date_field_${i}`}
                                        milestones={props.sponsor.currentMilestones}
                                        milestoneId={i}
                                        keyPrefix={props.keyPrefix || ''}
                                        amountOnly={true}
                                    />
                                </React.Fragment>
                            );
                        })}
                    <td className="tw-p-4 tw-text-right tw-border-r-2"></td>
                    <td className="tw-p-4 tw-text-right tw-border-r-2">{formatFigure(totals.totalValue)}</td>
                    <td className="tw-p-4 tw-text-center"></td>
                </tr>
            )}
            {showChildren && (
                <>
                    {hasUncommittedData && (
                        <UncommittedSection
                            {...props}
                            key={`uncommitted_sponsor_${props.sponsor.id}`}
                            sponsor={props.sponsor}
                            periods={periodsKeys}
                            maxSponsorMilestonesCount={maxSponsorMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}
                    {hasCommittedData && (
                        <CommittedSection
                            {...props}
                            key={`committed_sponsor_${props.sponsor.id}`}
                            sponsor={props.sponsor}
                            periods={periodsKeys}
                            maxSponsorMilestonesCount={maxSponsorMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}
                </>
            )}
        </>
    );
}
